import { exponentialCost, MAX_BUILDING_LEVEL, MAX_TOOL_TIER } from './balance.ts';
import { BUILDING_SUPPORT_RESOURCES_BY_SKILL } from './catalog.ts';
import type { GameState } from './types.ts';
import { recordProgress } from './progress.ts';
import { doctrineHallCostMultiplier, doctrineSaleMultiplier, doctrineToolCostMultiplier } from './doctrines.ts';
import { artifactHallCostMultiplier, artifactSaleMultiplier, artifactToolCostMultiplier } from './artifacts.ts';
import { constellationHallCostMultiplier } from './constellation.ts';

export const RESOURCE_SELL_VALUE = 0.2;
export const TOOL_BASE_COST = 5;
export const TOOL_COST_GROWTH = 1.35;
export const TOOL_LEVEL_STEP = 5;
export const TOOL_HALL_LEAD = 5;
export const BUILDING_BASE_COST = 25;
export const BUILDING_COST_GROWTH = 1.18;
export const BUILDING_LATE_COST_GROWTH = 1.45;
export const BUILDING_LATE_CURVE_LEVEL = 15;

export type ResourceCost = Record<string, number>;

export interface EconomyActionResult {
  ok: boolean;
  state: GameState;
  reason?: string;
  cost?: number;
  gainedGold?: number;
}

export interface ToolUpgradeRequirements {
  nextTier: number;
  skillLevel: number;
  hallLevel: number;
  meetsSkillLevel: boolean;
  meetsHallLevel: boolean;
  atMaxTier: boolean;
}

export function toolUpgradeRequirements(state: GameState): ToolUpgradeRequirements {
  const active = state.run.skills[state.run.activeSkill];
  const atMaxTier = active.toolTier >= MAX_TOOL_TIER;
  const nextTier = Math.min(MAX_TOOL_TIER, active.toolTier + 1);
  const skillLevel = nextTier === 1 ? 1 : (nextTier - 1) * TOOL_LEVEL_STEP;
  const hallLevel = Math.max(0, nextTier - TOOL_HALL_LEAD);
  return {
    nextTier,
    skillLevel,
    hallLevel,
    meetsSkillLevel: active.level >= skillLevel,
    meetsHallLevel: active.buildingLevel >= hallLevel,
    atMaxTier
  };
}

export function toolUpgradeCost(state: GameState): number {
  const tier = state.run.skills[state.run.activeSkill].toolTier;
  return tier >= MAX_TOOL_TIER ? Number.POSITIVE_INFINITY : Math.max(1, Math.ceil(exponentialCost(TOOL_BASE_COST, TOOL_COST_GROWTH, tier) * doctrineToolCostMultiplier(state) * artifactToolCostMultiplier(state)));
}

export function buildingUpgradeCost(state: GameState): number {
  const owned = state.run.skills[state.run.activeSkill].buildingLevel;
  if (owned >= MAX_BUILDING_LEVEL) return Number.POSITIVE_INFINITY;
  const earlyLevels = Math.min(owned, BUILDING_LATE_CURVE_LEVEL);
  const lateLevels = Math.max(0, owned - BUILDING_LATE_CURVE_LEVEL);
  return Math.max(1, Math.ceil(doctrineHallCostMultiplier(state) * artifactHallCostMultiplier(state) * constellationHallCostMultiplier(state) * (
    BUILDING_BASE_COST * BUILDING_COST_GROWTH ** earlyLevels * BUILDING_LATE_COST_GROWTH ** lateLevels
  )));
}

export function resourceSellValue(state: GameState): number {
  return RESOURCE_SELL_VALUE * 1.05 ** (state.prestige.upgrades.resonantExchange ?? 0) * doctrineSaleMultiplier(state) * artifactSaleMultiplier(state);
}

export function buildingUpgradeRecipe(state: GameState): ResourceCost {
  const active = state.run.skills[state.run.activeSkill];
  if (active.buildingLevel >= MAX_BUILDING_LEVEL) return {};
  const total = buildingUpgradeCost(state);
  const [first, second] = BUILDING_SUPPORT_RESOURCES_BY_SKILL[active.id];
  return {
    [first]: Math.ceil(total / 3),
    [second]: Math.ceil(total * 2 / 3)
  };
}

export function canAffordResources(state: GameState, cost: ResourceCost): boolean {
  return Object.entries(cost).every(([resource, amount]) => (state.run.resources[resource] ?? 0) >= amount);
}

export function sellResource(state: GameState, resource: string, requestedQuantity: number, now = Date.now()): EconomyActionResult {
  const available = Math.max(0, state.run.resources[resource] ?? 0);
  if (!Number.isFinite(requestedQuantity) || requestedQuantity <= 0 || available <= 0) {
    return { ok: false, state, reason: 'No resources available to sell.' };
  }
  const quantity = Math.min(available, requestedQuantity);
  const gainedGold = quantity * resourceSellValue(state);
  const nextState = recordProgress({
    ...state,
    updatedAt: now,
    revision: state.revision + 1,
    run: {
      ...state.run,
      gold: state.run.gold + gainedGold,
      earnedGold: state.run.earnedGold + gainedGold,
      resources: { ...state.run.resources, [resource]: available - quantity }
    },
    stats: {
      ...state.stats,
      totalGoldEarned: state.stats.totalGoldEarned + gainedGold,
      resourcesSold: (state.stats.resourcesSold ?? 0) + quantity,
      largestSaleGold: Math.max(state.stats.largestSaleGold ?? 0, gainedGold),
      resourcesSoldByResource: {
        ...(state.stats.resourcesSoldByResource ?? {}),
        [resource]: (state.stats.resourcesSoldByResource?.[resource] ?? 0) + quantity
      }
    }
  }).state;
  return {
    ok: true,
    gainedGold,
    state: nextState
  };
}

export function buyActiveToolUpgrade(state: GameState, now = Date.now()): EconomyActionResult {
  const active = state.run.skills[state.run.activeSkill];
  const cost = toolUpgradeCost(state);
  const requirements = toolUpgradeRequirements(state);
  if (requirements.atMaxTier) return { ok: false, state, cost, reason: 'Tool is already at maximum tier.' };
  if (!requirements.meetsSkillLevel) {
    return { ok: false, state, cost, reason: `Requires ${active.id} level ${requirements.skillLevel}.` };
  }
  if (!requirements.meetsHallLevel) {
    return { ok: false, state, cost, reason: `Requires ${active.id} Hall ${requirements.hallLevel}.` };
  }
  if (state.run.gold < cost) return { ok: false, state, cost, reason: 'Not enough gold.' };
  const nextState = recordProgress({
    ...state,
    updatedAt: now,
    revision: state.revision + 1,
    run: {
      ...state.run,
      gold: state.run.gold - cost,
      skills: { ...state.run.skills, [active.id]: { ...active, toolTier: active.toolTier + 1 } }
    },
    stats: {
      ...state.stats,
      toolsPurchased: (state.stats.toolsPurchased ?? 0) + 1,
      toolsPurchasedBySkill: {
        ...(state.stats.toolsPurchasedBySkill ?? {}),
        [active.id]: (state.stats.toolsPurchasedBySkill?.[active.id] ?? 0) + 1
      }
    }
  }).state;
  return {
    ok: true,
    cost,
    state: nextState
  };
}

export function buyActiveBuildingUpgrade(state: GameState, now = Date.now()): EconomyActionResult {
  const active = state.run.skills[state.run.activeSkill];
  const cost = buildingUpgradeCost(state);
  if (active.buildingLevel >= MAX_BUILDING_LEVEL) {
    return { ok: false, state, cost, reason: 'Hall is already at maximum level.' };
  }
  const recipe = buildingUpgradeRecipe(state);
  if (!canAffordResources(state, recipe)) {
    const missing = Object.entries(recipe)
      .filter(([resource, amount]) => (state.run.resources[resource] ?? 0) < amount)
      .map(([resource]) => resource)
      .join(' and ');
    return { ok: false, state, cost, reason: `Not enough ${missing}.` };
  }
  const resources = { ...state.run.resources };
  for (const [resource, amount] of Object.entries(recipe)) resources[resource] -= amount;
  const nextState = recordProgress({
    ...state,
    updatedAt: now,
    revision: state.revision + 1,
    run: {
      ...state.run,
      resources,
      skills: { ...state.run.skills, [active.id]: { ...active, buildingLevel: active.buildingLevel + 1 } }
    },
    stats: {
      ...state.stats,
      hallsPurchased: (state.stats.hallsPurchased ?? 0) + 1,
      hallsPurchasedBySkill: {
        ...(state.stats.hallsPurchasedBySkill ?? {}),
        [active.id]: (state.stats.hallsPurchasedBySkill?.[active.id] ?? 0) + 1
      }
    }
  }).state;
  return {
    ok: true,
    cost,
    state: nextState
  };
}
